/** Empty and error states (used everywhere a list or request can be empty or fail). */
import clsx from 'clsx';
import { CircleAlert, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { HttpError, errorMessage, isNetworkError } from '../lib/api.js';
import { Button } from './Button.js';
import styles from './ui.module.css';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ icon, title, children, action, compact }: EmptyStateProps) {
  return (
    <div className={clsx(styles.state, compact && styles.stateCompact)}>
      {icon && <div className={styles.stateIcon}>{icon}</div>}
      <p className={styles.stateTitle}>{title}</p>
      {children && <div className={styles.stateText}>{children}</div>}
      {action && <div className={styles.stateAction}>{action}</div>}
    </div>
  );
}

function isOffline(err: unknown): boolean {
  return isNetworkError(err) || (typeof navigator !== 'undefined' && navigator.onLine === false);
}

/** Readable title + text for any error. */
export function describeError(err: unknown): { title: string; text: string } {
  if (isOffline(err)) return { title: 'Geen verbinding', text: 'Controleer je internetverbinding en probeer het opnieuw.' };
  if (err instanceof HttpError) {
    // The app (not the user) lacks a Microsoft 365 permission.
    if (err.body?.error === 'graph_permission') return { title: 'Nog niet beschikbaar', text: errorMessage(err) };
    if (err.status === 403) return { title: 'Geen toegang', text: errorMessage(err) };
    if (err.status === 404) return { title: 'Niet gevonden', text: errorMessage(err) };
    if (err.status >= 500) return { title: 'Serverfout', text: 'De server kon het verzoek niet verwerken. Probeer het over een paar minuten opnieuw.' };
  }
  return { title: 'Er ging iets mis', text: errorMessage(err) };
}

/** Small inline error line (forms, dialogs); renders nothing without a message. */
export function InlineError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className={styles.inlineError}>
      <CircleAlert aria-hidden="true" />
      <span>{message}</span>
    </p>
  );
}

export interface ErrorStateProps {
  error?: unknown;
  title?: string;
  message?: string;
  onRetry?: () => void;
  compact?: boolean;
}

export function ErrorState({ error, title, message, onRetry, compact }: ErrorStateProps) {
  const d = describeError(error);
  return (
    <div className={clsx(styles.state, compact && styles.stateCompact)} role="alert">
      <div className={clsx(styles.stateIcon, styles.stateIconDanger)}>{isOffline(error) ? <WifiOff /> : <CircleAlert />}</div>
      <p className={styles.stateTitle}>{title ?? d.title}</p>
      <div className={styles.stateText}>{message ?? d.text}</div>
      {onRetry && (
        <div className={styles.stateAction}>
          <Button icon={<RefreshCw />} onClick={onRetry}>
            Opnieuw proberen
          </Button>
        </div>
      )}
    </div>
  );
}
